import { eq } from "drizzle-orm";

import { client } from "@/config";
import { db } from "@/db/db";
import { workspaceMembershipTag } from "@/db/schema";
import { createLogger } from "@/logger";
import { BootstrapEventsRegistry } from "@/models/BootstrapEventsRegistry";
import { bulkUpsertTag, replaceAllTags } from "@/models/repositories/Tag";
import {
  bulkUpsertWorkspaceMembershipTag,
  replaceAllWorkspaceMembershipTags,
} from "@/models/repositories/WorkspaceMembershipTag";

const log = createLogger("bootstrap");

const POLL_INTERVAL_MS = 60_000;
const PRUNE_INTERVAL_MS = 60_000;
export const FULL_SWEEP_INTERVAL_MS = 24 * 60 * 60 * 1000;

const TAGS_INCREMENTAL_CURSOR = "tagsIncrementalCursor";
const TAGS_FULL_SWEEP = "tagsFullSweep";
const MEMBERSHIP_TAGS_INCREMENTAL_CURSOR = "membershipTagsIncrementalCursor";
const MEMBERSHIP_TAGS_FULL_SWEEP = "membershipTagsFullSweep";

/**
 * Read/write side of the persistent cursor + last-full-sweep timestamp store. Production code
 * passes through to {@link BootstrapEventsRegistry} (IndexedDB-backed); tests inject an in-memory
 * stub so they don't need a real IndexedDB.
 */
export type CursorStore = {
  getEventDate: (event: string, workspaceId: string) => Promise<Date | undefined>;
  setEventDate: (event: string, workspaceId: string, date: Date) => Promise<void>;
};

export type TagSyncTarget = {
  /** Stable name used for the incremental cursor key in the cursor store. */
  cursorEvent: string;
  /** Stable name used for the last-full-sweep timestamp key in the cursor store. */
  fullSweepEvent: string;
  /**
   * Issues the network request. `timestamp` is undefined for full sweeps; the most recent cursor
   * for incrementals.
   */
  fetch: (workspaceId: string, timestamp?: string) => Promise<readonly unknown[]>;
  /** Merge a delta into the local DB via the existing bulk-upsert path. */
  applyIncremental: (records: readonly unknown[]) => Promise<void>;
  /** Replace the local table contents with a fresh server snapshot. */
  applyFull: (records: readonly unknown[]) => Promise<void>;
};

export type TagPollerDeps = {
  store: CursorStore;
  targets: readonly TagSyncTarget[];
  now: () => Date;
  fullSweepIntervalMs: number;
  /**
   * When this returns false, the poll was superseded (e.g. workspace switch) and must not apply
   * results or advance cursors — full sweeps use global replace helpers that would corrupt the
   * active workspace's local DB if a stale response were applied.
   */
  isPollStillActive?: (workspaceId: string) => boolean;
};

const registryStore: CursorStore = {
  getEventDate: (event, workspaceId) => BootstrapEventsRegistry.getInstance().getLatestEventDate(event, workspaceId),
  setEventDate: async (event, workspaceId, date) => {
    await BootstrapEventsRegistry.getInstance().markBootstrap(event, undefined, workspaceId, date);
  },
};

const tagsTarget: TagSyncTarget = {
  cursorEvent: TAGS_INCREMENTAL_CURSOR,
  fullSweepEvent: TAGS_FULL_SWEEP,
  fetch: async (workspaceId, timestamp) => {
    const response = await client.bootstrapWorkspaceTags({ workspaceId, timestamp });
    return response?.tags ?? [];
  },
  applyIncremental: async (records) => {
    await bulkUpsertTag(records as Parameters<typeof bulkUpsertTag>[0]);
  },
  applyFull: async (records) => {
    await replaceAllTags(records as Parameters<typeof replaceAllTags>[0]);
  },
};

const membershipTagsTarget: TagSyncTarget = {
  cursorEvent: MEMBERSHIP_TAGS_INCREMENTAL_CURSOR,
  fullSweepEvent: MEMBERSHIP_TAGS_FULL_SWEEP,
  fetch: async (workspaceId, timestamp) => {
    const response = await client.bootstrapWorkspaceMembershipTags({ workspaceId, timestamp });
    return response?.workspaceMembershipTags ?? [];
  },
  applyIncremental: async (records) => {
    await bulkUpsertWorkspaceMembershipTag(records as Parameters<typeof bulkUpsertWorkspaceMembershipTag>[0]);
  },
  applyFull: async (records) => {
    await replaceAllWorkspaceMembershipTags(records as Parameters<typeof replaceAllWorkspaceMembershipTags>[0]);
  },
};

const productionDeps: TagPollerDeps = {
  store: registryStore,
  targets: [tagsTarget, membershipTagsTarget],
  now: () => new Date(),
  fullSweepIntervalMs: FULL_SWEEP_INTERVAL_MS,
  isPollStillActive: (workspaceId) => activeWorkspaceId === workspaceId,
};

let activeWorkspaceId: string | null = null;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let prunerTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Stops any in-progress polling for the previous workspace and starts polling tag/membership-tag
 * changes for `workspaceId`. The first tick fires immediately so a freshly-loaded workspace picks
 * up any drift relative to its on-disk cache without waiting a full minute.
 */
export function startTagPolling(workspaceId: string): void {
  if (!workspaceId) return;
  if (activeWorkspaceId === workspaceId && pollTimer) return;

  stopTagPolling();
  activeWorkspaceId = workspaceId;

  log.info("Starting tag polling", { workspaceId });

  void pollOnce(workspaceId, productionDeps);
  pollTimer = setInterval(() => {
    if (!activeWorkspaceId) return;
    void pollOnce(activeWorkspaceId, productionDeps);
  }, POLL_INTERVAL_MS);

  startPruner();
}

/**
 * Stops the tag poller. Safe to call repeatedly. The pruner stays running because deleting
 * tombstoned membership-tag rows is a workspace-agnostic local-DB cleanup and incurs no network
 * cost — keeping it ticking avoids resurrecting it on every workspace switch.
 */
export function stopTagPolling(): void {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  activeWorkspaceId = null;
}

function startPruner(): void {
  if (prunerTimer) return;
  prunerTimer = setInterval(async () => {
    try {
      await db.delete(workspaceMembershipTag).where(eq(workspaceMembershipTag.enabled, false));
    } catch (error) {
      log.warn("Pruner failed to delete disabled membership tags", { error });
    }
  }, PRUNE_INTERVAL_MS);
}

/**
 * Runs a single poll tick across every configured tag target. Exported so tests can drive the
 * poller deterministically without spinning up real timers, and so callers can force-refresh on
 * demand if a future feature ever needs that hook.
 */
export async function pollOnce(workspaceId: string, deps: TagPollerDeps = productionDeps): Promise<void> {
  await Promise.all(deps.targets.map((target) => syncTarget(workspaceId, target, deps)));
}

function pollWorkspaceStillActive(deps: TagPollerDeps, workspaceId: string): boolean {
  return deps.isPollStillActive?.(workspaceId) ?? true;
}

async function syncTarget(workspaceId: string, target: TagSyncTarget, deps: TagPollerDeps): Promise<void> {
  if (!pollWorkspaceStillActive(deps, workspaceId)) return;

  const lastFullSweep = await deps.store.getEventDate(target.fullSweepEvent, workspaceId);
  const fullSweepDue = !lastFullSweep || deps.now().getTime() - lastFullSweep.getTime() >= deps.fullSweepIntervalMs;

  if (!pollWorkspaceStillActive(deps, workspaceId)) return;

  if (fullSweepDue) {
    await runFullSweep(workspaceId, target, deps);
    return;
  }

  await runIncremental(workspaceId, target, deps);
}

async function runFullSweep(workspaceId: string, target: TagSyncTarget, deps: TagPollerDeps): Promise<void> {
  const sweptAt = deps.now();
  try {
    if (!pollWorkspaceStillActive(deps, workspaceId)) return;

    const records = await target.fetch(workspaceId);
    if (!pollWorkspaceStillActive(deps, workspaceId)) return;

    await target.applyFull(records);
    await deps.store.setEventDate(target.fullSweepEvent, workspaceId, sweptAt);
    await deps.store.setEventDate(target.cursorEvent, workspaceId, sweptAt);
    log.info("Full sweep complete", {
      workspaceId,
      event: target.fullSweepEvent,
      count: records.length,
      sweptAt: sweptAt.toISOString(),
    });
  } catch (error) {
    log.warn("Full sweep failed", { workspaceId, event: target.fullSweepEvent, error });
  }
}

async function runIncremental(workspaceId: string, target: TagSyncTarget, deps: TagPollerDeps): Promise<void> {
  const cursorDate = await deps.store.getEventDate(target.cursorEvent, workspaceId);
  if (!pollWorkspaceStillActive(deps, workspaceId)) return;

  const sentTimestamp = cursorDate?.toISOString();
  const requestStart = deps.now();

  try {
    if (!pollWorkspaceStillActive(deps, workspaceId)) return;

    const records = await target.fetch(workspaceId, sentTimestamp);
    if (!pollWorkspaceStillActive(deps, workspaceId)) return;

    if (records.length > 0) {
      await target.applyIncremental(records);
    }
    if (!pollWorkspaceStillActive(deps, workspaceId)) return;

    await deps.store.setEventDate(target.cursorEvent, workspaceId, requestStart);
    log.info("Incremental sync complete", {
      workspaceId,
      event: target.cursorEvent,
      count: records.length,
      sentTimestamp,
      nextCursor: requestStart.toISOString(),
    });
  } catch (error) {
    log.warn("Incremental sync failed", { workspaceId, event: target.cursorEvent, error });
  }
}

/**
 * Test-only reset hook. Clears in-memory state between tests so each test starts from a clean
 * slate; production code never resets these fields directly.
 */
export function __resetTagPollerForTests(): void {
  stopTagPolling();
  if (prunerTimer) {
    clearInterval(prunerTimer);
    prunerTimer = null;
  }
}
